import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { ArrowLeft, MapPin, ExternalLink, Users, DollarSign, Award, Target, Star } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useCollegeContext } from '../../contexts/CollegeContext';
import { useFamilyContext } from '../../contexts/FamilyContext';
import EnhancedRankingDisplay from './EnhancedRankingDisplay';

const GlobalCollegeView = ({ globalCollegeId }) => {
  const navigate = useNavigate();
  const { addCollege } = useCollegeContext();
  const { currentUser, familyId } = useFamilyContext();
  const [college, setCollege] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addingToList, setAddingToList] = useState(false);

  useEffect(() => {
    const loadCollegeData = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from('global_colleges')
          .select('*')
          .eq('id', globalCollegeId)
          .single();

        if (fetchError) {
          throw fetchError;
        }

        if (!data) {
          throw new Error('College not found');
        }

        setCollege(data);
      } catch (err) {
        console.error('Error loading global college:', err);
        setError(err.message || 'Failed to load college data');
      } finally {
        setLoading(false);
      }
    };

    if (globalCollegeId) {
      loadCollegeData();
    }
  }, [globalCollegeId]);

  const handleAddToMySchools = async (category = 'target') => {
    if (!college || !currentUser) return;

    try {
      setAddingToList(true);

      const collegeData = {
        name: college.name,
        official_name: college.official_name || college.name,
        location: `${college.city || ''}, ${college.state || ''}`.trim().replace(/^,\s*|,\s*$/g, ''),
        website: college.website,
        global_college_id: college.id,
        category: category,
        status: 'researching',
        type: college.type,
        user_id: currentUser.id,
        family_id: familyId,
        // Copy ranking and data fields
        usnwr_ranking: college.usnwr_ranking,
        liberal_arts_ranking: college.liberal_arts_ranking,
        engineering_ranking: college.engineering_ranking,
        nursing_ranking: college.nursing_ranking,
        business_ranking: college.business_ranking,
        computer_science_ranking: college.computer_science_ranking,
        acceptance_rate: college.acceptance_rate,
        tuition_in_state: college.tuition_in_state,
        tuition_out_of_state: college.tuition_out_of_state,
        student_enrollment: college.student_enrollment,
        academic_focus: college.academic_focus,
        test_optional: college.test_optional,
        test_blind: college.test_blind
      };

      await addCollege(collegeData, currentUser.id);
      toast.success(`Added ${college.name} to your ${category} schools`);
      
      // Navigate to the college strategy page for the newly added college
      // We'll need to fetch the new college ID, but for now, navigate to college list
      navigate('/college');
    } catch (error) {
      console.error('Error adding college to list:', error);
      toast.error('Failed to add college to your list');
    } finally {
      setAddingToList(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-azure-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading college information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-red-900 mb-2">College Not Found</h2>
            <p className="text-red-700 mb-4">{error}</p>
            <Link 
              to="/college/colleges"
              className="inline-flex items-center gap-2 px-4 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to College Search
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!college) {
    return null;
  }

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link 
                to="/college/colleges"
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span className="text-sm font-medium">Back to Search</span>
              </Link>
              <div className="hidden sm:block w-px h-6 bg-stone-300"></div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{college.name}</h1>
                {college.official_name && college.official_name !== college.name && (
                  <p className="text-sm text-slate-600">{college.official_name}</p>
                )}
              </div>
            </div>
            
            {/* Add to My Schools Button */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => handleAddToMySchools('target')}
                disabled={addingToList}
                className="flex items-center gap-2 px-4 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 transition-colors disabled:opacity-50"
              >
                <Star className="w-4 h-4" />
                {addingToList ? 'Adding...' : 'Add to My Schools'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Left Column - Basic Info */}
          <div className="lg:col-span-1 space-y-6">
            
            {/* Location & Basic Info */}
            <div className="bg-white rounded-lg border border-stone-200 p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Basic Information</h3>
              
              <div className="space-y-4">
                {(college.city || college.state) && (
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-stone-400" />
                    <span className="text-slate-900">
                      {[college.city, college.state].filter(Boolean).join(', ')}
                    </span>
                  </div>
                )}
                
                {college.type && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700">Institution Type</span>
                    <span className="text-sm text-slate-900 capitalize">{college.type}</span>
                  </div>
                )}
                
                {college.ranking_type && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700">Classification</span>
                    <span className="text-sm text-slate-900">
                      {college.ranking_type === 'national_university' ? 'National University' : 'Liberal Arts College'}
                    </span>
                  </div>
                )}
                
                {college.academic_focus && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700">Academic Focus</span>
                    <span className="text-sm text-slate-900">{college.academic_focus}</span>
                  </div>
                )}
                
                {college.website && (
                  <div className="pt-4 border-t border-stone-200">
                    <a
                      href={college.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-azure-600 hover:text-azure-700 font-medium"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Visit Website
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* Test Requirements */}
            {(college.test_optional || college.test_blind) && (
              <div className="bg-white rounded-lg border border-stone-200 p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Test Requirements</h3>
                <div className="space-y-2">
                  {college.test_optional && (
                    <div className="flex items-center gap-2 text-emerald-700">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                      <span className="text-sm">Test Optional</span>
                    </div>
                  )}
                  {college.test_blind && (
                    <div className="flex items-center gap-2 text-emerald-700">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                      <span className="text-sm">Test Blind</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Rankings & Stats */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Rankings Section */}
            <div className="bg-white rounded-lg border border-stone-200 p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Rankings</h3>
              <EnhancedRankingDisplay college={college} layout="detailed" />
            </div>

            {/* Statistics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* Admissions Stats */}
              <div className="bg-white rounded-lg border border-stone-200 p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Admissions</h3>
                <div className="space-y-4">
                  {college.acceptance_rate && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-700">Acceptance Rate</span>
                      <span className="text-lg font-semibold text-slate-900">{college.acceptance_rate}%</span>
                    </div>
                  )}
                  
                  {college.student_enrollment && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-700">Total Enrollment</span>
                      <span className="text-lg font-semibold text-slate-900">
                        {college.student_enrollment.toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Financial Info */}
              <div className="bg-white rounded-lg border border-stone-200 p-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-4">Tuition & Costs</h3>
                <div className="space-y-4">
                  {college.tuition_in_state && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-700">In-State Tuition</span>
                      <span className="text-lg font-semibold text-slate-900">
                        ${college.tuition_in_state.toLocaleString()}
                      </span>
                    </div>
                  )}
                  
                  {college.tuition_out_of_state && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-700">Out-of-State Tuition</span>
                      <span className="text-lg font-semibold text-slate-900">
                        ${college.tuition_out_of_state.toLocaleString()}
                      </span>
                    </div>
                  )}
                  
                  {college.room_board && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-700">Room & Board</span>
                      <span className="text-lg font-semibold text-slate-900">
                        ${college.room_board.toLocaleString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Add to List Categories */}
            <div className="bg-white rounded-lg border border-stone-200 p-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Add to Your College List</h3>
              <p className="text-slate-600 mb-4">
                Choose a category to add {college.name} to your personal college list:
              </p>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <button
                  onClick={() => handleAddToMySchools('reach')}
                  disabled={addingToList}
                  className="flex items-center justify-center gap-2 p-4 border-2 border-violet-200 text-violet-700 rounded-lg hover:bg-violet-50 hover:border-violet-300 transition-colors disabled:opacity-50"
                >
                  <Award className="w-5 h-5" />
                  <span className="font-medium">reach School</span>
                </button>
                
                <button
                  onClick={() => handleAddToMySchools('target')}
                  disabled={addingToList}
                  className="flex items-center justify-center gap-2 p-4 border-2 border-azure-200 text-azure-700 rounded-lg hover:bg-azure-50 hover:border-azure-300 transition-colors disabled:opacity-50"
                >
                  <Target className="w-5 h-5" />
                  <span className="font-medium">Target School</span>
                </button>
                
                <button
                  onClick={() => handleAddToMySchools('safety')}
                  disabled={addingToList}
                  className="flex items-center justify-center gap-2 p-4 border-2 border-emerald-200 text-emerald-700 rounded-lg hover:bg-emerald-50 hover:border-emerald-300 transition-colors disabled:opacity-50"
                >
                  <Star className="w-5 h-5" />
                  <span className="font-medium">Safety School</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalCollegeView;